import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useJsApiLoader, GoogleMap as GoogleMapWrapper } from '@react-google-maps/api';
import useFilteredResults from '@unmapped/core/hooks/useFilteredResults';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import MapMarkerSpinner from "../spinners/MapMarkerSpinner/index.js";
import useStyleOverride from "../hooks/useStyleOverride.js";
import { decorateName } from '@unmapped/core/utils';
import { observer } from 'mobx-react-lite';
import useMap from "../hooks/useMap.js";
import baseStyles from "./styles.module.css.js";
var defaultHidablePOI = {
  attraction: false,
  business: true,
  government: false,
  medical: false,
  park: false,
  place_of_worship: true,
  school: true,
  sports_complex: true
};
var GOOGLE_MAPS_LIBRARIES = ['places'];
export var GoogleMaps = decorateName(observer(_ref => {
  var {
    apiKey,
    mapRef,
    onMarkerClick,
    children,
    markerIcon,
    onClusterUpdate,
    clustererProps = {},
    showMarkers = true,
    clusterMarkers = true,
    initialCenter = ['results', {
      lat: 41.889813,
      lng: -87.628687
    }],
    autoCenterOnResultUpdate = true,
    hidePOIs = {},
    mapStyles: _mapStyles
  } = _ref;
  var {
    setHandle,
    map
  } = useMap();
  if (mapRef) mapRef.current = map;
  var results = useFilteredResults();
  var onMarkerClickRef = useRef(null);
  onMarkerClickRef.current = onMarkerClick !== null && onMarkerClick !== void 0 ? onMarkerClick : null;
  var indexedResultsRef = useRef();
  useEffect(() => {
    indexedResultsRef.current = results.reduce((r, item) => _objectSpread(_objectSpread({}, r), {}, {
      [item.id]: item
    }), {});
  }, [results]);
  var styles = useStyleOverride('GoogleMaps', baseStyles);
  var {
    isLoaded,
    loadError
  } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES
  });
  var mapStyles = useMemo(() => {
    var patchedStyles = _mapStyles ? [..._mapStyles] : [];
    if (hidePOIs === true) {
      Object.keys(defaultHidablePOI).forEach(poiKind => {
        patchedStyles.push({
          featureType: "poi.".concat(poiKind),
          elementType: 'labels.icon',
          stylers: [{
            visibility: 'off'
          }]
        });
      });
    } else if (hidePOIs === false) {
      Object.keys(defaultHidablePOI).forEach(poiKind => {
        patchedStyles.push({
          featureType: "poi.".concat(poiKind),
          elementType: 'labels.icon',
          stylers: [{
            visibility: 'on'
          }]
        });
      });
    } else {
      var patchedPOIs = _objectSpread(_objectSpread({}, defaultHidablePOI), hidePOIs);
      Object.keys(defaultHidablePOI).forEach(poiKind => {
        patchedStyles.push({
          featureType: "poi.".concat(poiKind),
          elementType: 'labels.icon',
          stylers: [{
            visibility: patchedPOIs[poiKind] ? 'off' : 'on'
          }]
        });
      });
    }
    return patchedStyles.length ? patchedStyles : undefined;
  }, [_mapStyles, hidePOIs]);
  var onMapLoad = useCallback(map => {
    setHandle(map);
    if (Array.isArray(initialCenter)) {
      var _initialCenter$;
      map.setCenter((_initialCenter$ = initialCenter[1]) !== null && _initialCenter$ !== void 0 ? _initialCenter$ : {
        lat: 41.889813,
        lng: -87.628687
      });
    } else {
      map.setCenter(initialCenter);
    }
  }, []);
  var clustererRef = useRef(null);
  var markerToIdIndex = useMemo(() => new Map(), []);
  var markerIndex = useMemo(() => new Map(), []);
  useEffect(() => {
    if (!map || !autoCenterOnResultUpdate) return;
    var ids = new Set(results.map(result => result.id));
    var bounds = new google.maps.LatLngBounds();
    var clusterer = clustererRef.current;
    if (showMarkers) {
      var clustererNew = false;
      if (clusterMarkers && !clusterer) {
        clustererNew = true;
        clusterer = clustererRef.current = new MarkerClusterer({
          map,
          markers: [],
          algorithm: clustererProps.algorithm,
          onClusterClick: clustererProps.onClusterClick,
          renderer: {
            render: 'render' in clustererProps ? clustererProps.render : cluster => {
              var _clustererProps$label;
              return new google.maps.Marker({
                label: clustererProps.label === false ? undefined : _objectSpread({
                  color: '#000',
                  fontSize: '8px',
                  text: String(cluster.count),
                  className: styles.ClusterIconLabel,
                  fontFamily: '"Roboto", sans-serif'
                }, typeof clustererProps.label === 'function' ? clustererProps.label(cluster.count) : (_clustererProps$label = clustererProps.label) !== null && _clustererProps$label !== void 0 ? _clustererProps$label : {}),
                position: cluster.position,
                icon: clustererProps.markerIcon,
                optimized: false,
                // adjust zIndex to be above other markers
                zIndex: Number(google.maps.Marker.MAX_ZINDEX) + cluster.count
              });
            }
          }
        });
        clusterer.addListener('clusteringend', clusterResult => {
          if (onClusterUpdate) {
            var _idToClusterIndex = {};
            for (var _cluster of clusterResult.clusters) {
              if (_cluster.markers && _cluster.markers.length > 1) {
                for (var marker of (_cluster$markers = _cluster.markers) !== null && _cluster$markers !== void 0 ? _cluster$markers : []) {
                  var _cluster$markers;
                  var id = markerToIdIndex.get(marker);
                  if (id) {
                    // record position of cluster to use instead
                    _idToClusterIndex[id] = _cluster;
                  }
                }
              }
            }
            onClusterUpdate(_idToClusterIndex);
          }
        });
      } else if (!clusterMarkers && clusterer) {
        // we were clustering markers, but now we stop doing so.. time to cleanup 
        for (var marker of markerIndex.values()) {
          marker.unbindAll();
          marker.setMap(null);
          clusterer.removeMarker(marker, true);
        }
        markerIndex.clear();
        clusterer.setMap(null);
        clustererRef.current = null;
      }
      // remove any old markers from the index
      for (var [id, _marker] of markerIndex.entries()) {
        if (!ids.has(id)) {
          _marker.setMap(null); // remove from map
          _marker.unbindAll();
          if (clusterMarkers && clustererRef.current) {
            clustererRef.current.removeMarker(_marker, true);
          }
          markerIndex.delete(id);
          markerToIdIndex.delete(_marker);
        }
      }
      // add any new markers to the index, adjust bounding box
      var _loop = function _loop(_result) {
        bounds.extend({
          lat: _result.lat,
          lng: _result.lng
        });
        if (!markerIndex.has(_result.id)) {
          var _marker2 = new google.maps.Marker({
            map: clusterMarkers ? undefined : map,
            position: new google.maps.LatLng(_result.lat, _result.lng),
            icon: markerIcon,
            optimized: false
          });
          markerToIdIndex.set(_marker2, _result.id);
          _marker2.setClickable(!!onMarkerClickRef.current);
          _marker2.addListener('click', e => {
            if (onMarkerClickRef.current && indexedResultsRef.current) {
              var currentResult = indexedResultsRef.current[_result.id];
              onMarkerClickRef.current(currentResult, e);
            }
          });
          if (clusterMarkers && clusterer) clusterer.addMarker(_marker2, true);
          markerIndex.set(_result.id, _marker2);
        } else {
          var _marker3 = markerIndex.get(_result.id);
          if (clustererNew && clusterMarkers && clusterer && _marker3) {
            // marker was already indexed, but now clustering is enabled so we'll add it to the cluster
            clusterer.addMarker(_marker3, true);
          }
        }
      };
      for (var _result of results) {
        _loop(_result);
      }

      // clustering is enabled, force a redraw
      if (clusterer) {
        clusterer.render();
      }
      if (autoCenterOnResultUpdate && results.length) {
        map.fitBounds(bounds);
      }
    } else {
      // we were showing markers but now we're not
      for (var _marker4 of markerIndex.values()) {
        markerToIdIndex.delete(_marker4);
        if (clusterer) {
          clusterer.removeMarker(_marker4, true);
        }
        _marker4.setMap(null); // remove marker from map
        _marker4.unbindAll();
      }
      if (clusterer) {
        clusterer.setMap(null);
        clusterer.unbindAll();
      }
      markerIndex.clear();
    }
  }, [!!map, results, autoCenterOnResultUpdate, showMarkers, clusterMarkers]);
  useEffect(() => {
    if (!map) return;
    for (var marker of markerIndex.values()) {
      marker.setIcon(markerIcon);
      marker.setClickable(!!onMarkerClick);
    }
  }, [markerIcon, onMarkerClick, clustererProps]);
  var content = null;
  if (isLoaded && !loadError) {
    content = /*#__PURE__*/React.createElement(GoogleMapWrapper, {
      mapContainerStyle: {
        width: '100%',
        height: '100%'
      },
      onLoad: onMapLoad,
      clickableIcons: false,
      zoom: 10,
      options: {
        controlSize: 24,
        styles: mapStyles,
        streetViewControl: false,
        mapTypeControl: false
      }
    }, children);
  } else if (loadError) {
    content = /*#__PURE__*/React.createElement("div", {
      className: styles.ErrorText
    }, "Google maps failed to load:", /*#__PURE__*/React.createElement("pre", null, JSON.stringify(loadError)));
  } else {
    content = /*#__PURE__*/React.createElement(MapMarkerSpinner, null);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: styles.MapContainer
  }, content);
}), 'GoogleMaps');
export default GoogleMaps;
//# sourceMappingURL=index.js.map